import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Icon, Empty, Button } from 'antd';
import NewTabs from 'common/components/tabs';
import ASSIGNED_LANDLORDS from './queries';
import { useAddAllLandlord } from './hooks/addAllLandlord';
import ChooseLandlordModal from './chooseLandlordModal';
import InprogressBooking from './inProgress';
import MessageProperty from './message-property';
import ManualProperty from './manual-property';
import BookingList from './booking-list';
import { bookingJourneyType } from 'common/constants/booking';
import { isBCRole } from '~client/helpers/auth';

const Booking = () => {
  const { t } = useTranslation();

  const bookingHeaderRef = useRef(null);
  const [showLandlordModal, setShowLandlordModal] = useState(false);
  const [isSticked, setIsSticked] = useState(false);
  const [chosenTab, setChosenTab] = useState('IN_PROGRESS');

  const { data: assignedLandlord, refetch } = useQuery(ASSIGNED_LANDLORDS, {
    fetchPolicy: 'no-cache',
  });

  const { addAllLandlord, loading: addAllLandlordLoading } = useAddAllLandlord();

  const hasSelectedLandlords = useMemo(
    () => !!assignedLandlord && !!assignedLandlord?.listLandlordsManager?.landlordsManager?.length,
    [assignedLandlord],
  );

  const notSelectedLandlords = useMemo(
    () => !!assignedLandlord && !assignedLandlord?.listLandlordsManager?.landlordsManager?.length,
    [assignedLandlord],
  );

  const assignedLandlordString = useMemo(
    () => JSON.stringify(assignedLandlord),
    [assignedLandlord],
  );

  const statusList = [
    {
      text: t('cms:properties_booking_in_progress'),
      value: 'IN_PROGRESS',
    },
    {
      text: t('cms:properties_booking_type_lead_generation'),
      value: bookingJourneyType.LEAD_GENERATION,
    },
    {
      text: t('cms:properties_booking_type_semi_automatic'),
      value: bookingJourneyType.SEMI_AUTOMATIC,
    },
    {
      text: t('cms:properties_booking_type_full_automatic'),
      value: bookingJourneyType.FULL_AUTOMATIC,
    },
    {
      text: t('cms:properties_booking_type_manual'),
      value: bookingJourneyType.MANUAL,
    },
  ];

  useEffect(() => {
    const el = bookingHeaderRef.current;
    if (el) {
      const observer = new IntersectionObserver(
        ([e]) => {
          setIsSticked(e.intersectionRatio < 1);
        },
        {
          threshold: [1],
        },
      );
      observer.observe(el);

      return () => observer.unobserve(el);
    }
  });

  const Header = props => {
    return (
      <div
        className={classNames('booking__header', { 'booking-stickyHeader': isSticked })}
        ref={bookingHeaderRef}
      >
        <div className="booking-title">Booking</div>
        <If
          condition={
            props.hasSelectedLandlords &&
            !isBCRole() &&
            (chosenTab === 'IN_PROGRESS' || chosenTab === bookingJourneyType.SEMI_AUTOMATIC)
          }
        >
          <div className="booking__manageBtn" onClick={openLandlordModal}>
            <span>
              <Icon type="setting" style={{ fontSize: '14px' }} />
            </span>
            <span className="booking__manageBtn-title">
              {t('cms.booking.manage_landlord_list.btn')}
            </span>
          </div>
        </If>
      </div>
    );
  };

  const openLandlordModal = () => {
    setShowLandlordModal(true);
  };

  const closeLandlordModal = () => {
    setShowLandlordModal(false);
  };

  const NoSelectedLandlords = () => {
    return (
      <div className="booking__emptyBox">
        <Empty
          imageStyle={{
            height: 99,
          }}
          description={t('cms.booking.not_choose_landlord.hint')}
        >
          <Button
            className="booking__goBtn"
            style={{ marginRight: 20 }}
            loading={addAllLandlordLoading}
            onClick={addAllLandlord}
          >
            <Icon type="plus-circle" style={{ fontSize: '14px' }} />
            {t('cms.booking.choose_landlord.add_all')}
          </Button>
          <Button className="booking__goBtn" onClick={openLandlordModal}>
            <Icon type="setting" style={{ fontSize: '14px' }} />
            {t('cms.booking.choose_landlord.hint')}
          </Button>
        </Empty>
      </div>
    );
  };

  return (
    <div className="booking__wrapper">
      <Header hasSelectedLandlords={hasSelectedLandlords} />
      <div className="booking__contentsWrap">
        <NewTabs
          defaultActiveKey={0}
          onChange={e => {
            setChosenTab(e.value);
          }}
          options={statusList}
          tabPosition="top"
          className="property-status_tabs"
        />
        {(chosenTab === 'IN_PROGRESS' || chosenTab === bookingJourneyType.SEMI_AUTOMATIC) &&
          notSelectedLandlords && <NoSelectedLandlords key={assignedLandlordString} />}

        {chosenTab === 'IN_PROGRESS' && hasSelectedLandlords && (
          <InprogressBooking key={assignedLandlordString} />
        )}
        {chosenTab === bookingJourneyType.SEMI_AUTOMATIC && hasSelectedLandlords && (
          <MessageProperty t={t} key={assignedLandlordString} />
        )}
        {chosenTab === bookingJourneyType.MANUAL && (
          <ManualProperty t={t} bookingJourneys={[bookingJourneyType.MANUAL]} />
        )}
        {chosenTab === bookingJourneyType.LEAD_GENERATION && (
          <BookingList t={t} bookingJourneys={[bookingJourneyType.LEAD_GENERATION]} />
        )}
        {chosenTab === bookingJourneyType.FULL_AUTOMATIC && (
          <BookingList t={t} bookingJourneys={[bookingJourneyType.FULL_AUTOMATIC]} />
        )}
      </div>
      {showLandlordModal && (
        <ChooseLandlordModal
          activeModal={showLandlordModal}
          visible={showLandlordModal}
          refetch={refetch}
          onClose={closeLandlordModal}
          t={t}
        />
      )}
    </div>
  );
};

export default Booking;
